import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { isEmpty } from 'lodash';
import { DataTable } from '@/components/organism/Table';
import { SalesExpandedPanel } from '@/components/organism/TableExpand/SalesExpanded/SalesExpandedPanel';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  ALL_LOCATION,
  ALL_OPERATOR,
  STRINGS,
  DROPDOWN_OPTIONS,
  REPORT_FILE_NAMES,
  MODAL_TITLES,
} from '@/constant/global/global.constant';

import { useDispatch, useSelector } from 'react-redux';

import { getSelectedTimeZone } from '@/utils/dateTime';
import { clearMmsSalesError, getMmsSales } from '@/store/slices/mmsSales.slice';
import { getMmsSalesDetail } from '@/store/selectors/mmsSales.selector';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import {
  GET_MMS_SALES_BY_ORDER_ID_ACTION_TYPE,
  MMS_SALES_EFILTER,
  MMS_SALES_PAGE_UI_PROPS,
} from '@/constant/mmsSales/mmsSales.constants';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { useDebounce } from '@/hooks/useDebounce';
import { ERequestMethods } from '@/constant/downloadReport/downloadReport';
import {
  EAllowedFieldsForExport,
  EExportPage,
} from '@/components/organism/ExportDataModal/export.types';
import { useExportFormats } from '@/hooks/useExportFormats';
import { useDateFilter } from '@/context/dateFilter.context';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { MMS_SALES_TEST_IDS as TID } from '@/constant/testIds/mmsSales.testIds';
import { IMmsSalesProps } from './mmsSales.interface';
import { buildColumns } from './mmsSales.columns';
import { Tooltip } from '@/components/atoms';

/* Segmented-control options derived from filter constants */
const DATE_FILTER_OPTIONS = MMS_SALES_EFILTER.map((f) => ({
  value: f.queryParam,
  label: f.name,
}));

const MmsSales = () => {
  const [isCopy, setIsCopy] = useState('');
  // Track whether we've ever received data — used to distinguish initial load
  // (show skeleton) from sort/filter refetches (show thin progress bar only).
  const hasLoaded = useRef(false);
  const [sorting, setSorting] = useState({
    orderBy: 'orderDtm',
    order: 'desc',
  });
  const [isMsgOpen, setIsMsgOpen] = useState<Record<string, boolean>>({});
  const exportFormats = useExportFormats(EExportPage.MMS_SALES);
  const toggleMsgOpen = useCallback((id: string) => {
    setIsMsgOpen((prev) => {
      // Close all other rows, toggle the target
      const resetState = Object.keys(prev).reduce<Record<string, boolean>>(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {},
      );
      return { ...resetState, [id]: !prev[id] };
    });
  }, []);
  const selectedTimezone = getSelectedTimeZone();

  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [isExpandLoading, setIsExpandLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const dispatch = useDispatch();
  const [selection, setSelection] = useState({
    operator: ALL_OPERATOR,
    market: DROPDOWN_OPTIONS.ALL_MARKET_DROPDOWN_OPTION,
    device: DROPDOWN_OPTIONS.ALL_DEVICE_DROPDOWN_OPTION,
  });
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;

  const onTableRowClick = useCallback(
    (orderId: string) => {
      const isCurrentlyOpen = isMsgOpen[orderId] ?? false;
      toggleMsgOpen(orderId);
      if (!isCurrentlyOpen && selectedOrderId !== orderId) {
        setSelectedOrderId(orderId);
        setIsExpandLoading(true);
        dispatch({
          type: GET_MMS_SALES_BY_ORDER_ID_ACTION_TYPE,
          payload: { orderId },
        });
      }
    },
    [dispatch, isMsgOpen, toggleMsgOpen, selectedOrderId],
  );

  const [dateFilterBy, setDateFilterBy] = useState(MMS_SALES_EFILTER[0]);
  const [sourceOrderId, setSourceOrderId] = useState('');
  const sourceOrderIdQuery = useDebounce(
    sourceOrderId?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const {
    loading,
    mmsSales: {
      data,
      pagination: paginationResponse,
      message,
      success: AllRecordsSuccess,
    },
    mmsSalesById: {
      data: saleRecordData,
      message: saleRecordMessage,
      success: SingleRecordSuccess,
    },
    error,
  } = useSelector(getMmsSalesDetail);

  // Clear expand loader once the fetched record matches the selected orderId
  useEffect(() => {
    if ((saleRecordData as any)?.orderId === selectedOrderId) {
      setIsExpandLoading(false);
    }
  }, [(saleRecordData as any)?.orderId, selectedOrderId]);

  // Track first successful data load
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  const expandedRowIndex = useMemo<number | null>(() => {
    if (!data?.length) return null;
    const idx = (data as any[]).findIndex(
      (row: any) => isMsgOpen[row?.orderId] === true,
    );
    return idx >= 0 ? idx : null;
  }, [data, isMsgOpen]);

  useEffect(() => {
    if ((AllRecordsSuccess || SingleRecordSuccess) && error) {
      const errorMessage = message || saleRecordMessage;
    }
    dispatch(clearMmsSalesError());
  }, [error, saleRecordMessage]);

  const totalPages = Math.ceil(
    paginationResponse?.totalItems / pagination.limit,
  );

  const handleDateFilterByChange = useCallback((value: any) => {
    setDateFilterBy(value);
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((prev) => ({ ...prev, limit: value }));
      if (totalPages < pagination?.page)
        setPagination((prev) => ({ ...prev, page: totalPages }));
    },
    [totalPages, pagination?.page],
  );

  const handlePreviousClick = useCallback(
    (page: number) => {
      if (page > 1 && page <= totalPages)
        setPagination((prev) => ({ ...prev, page: page - 1 }));
    },
    [totalPages],
  );

  const handleNextClick = useCallback(
    (page: number) => {
      if (page >= 1 && page < totalPages)
        setPagination((prev) => ({ ...prev, page: page + 1 }));
    },
    [totalPages],
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 1500);
  }, []);

  const onHandleSourceOrderIdSearch = useCallback((value: string) => {
    setSourceOrderId(value);
  }, []);

  const mmsSalesParams = useMemo(() => {
    const startDateStr =
      startDate instanceof Date ? startDate.toISOString() : startDate;
    const endDateStr =
      endDate instanceof Date ? endDate.toISOString() : endDate;
    return {
      ...pagination,
      startDate: startDateStr,
      endDate: endDateStr,
      ...sorting,
      ...(selection?.operator && selection?.operator?.id !== ALL_OPERATOR.id
        ? { operatorId: selection?.operator?.id }
        : {}),
      marketId: selection?.market?.id,
      deviceId: selection?.device?.id,
      locationId: ALL_LOCATION.locationId,
      dateFilterBy: dateFilterBy?.queryParam,
      isCompressed: true,
      sourceOrderId: sourceOrderIdQuery,
    };
  }, [
    pagination,
    startDate,
    endDate,
    sorting,
    selection,
    dateFilterBy?.queryParam,
    sourceOrderIdQuery,
  ]);

  useEffect(() => {
    if (
      sourceOrderIdQuery &&
      (sourceOrderIdQuery?.length < 3 || sourceOrderIdQuery?.length > 32)
    )
      return;
    dispatch(getMmsSales({ ...mmsSalesParams }));
    setIsMsgOpen({});
  }, [
    pagination?.limit,
    pagination?.page,
    startDate,
    endDate,
    sorting,
    selection?.operator?.id,
    selection?.device?.id,
    selection?.market?.id,
    dateFilterBy?.queryParam,
    sourceOrderIdQuery,
  ]);

  const handleSelectionChange = useCallback(
    ({
      operator,
      market,
      device,
    }: {
      operator: any;
      market: any;
      device: any;
    }) => {
      setSelection({ operator, market, device });
    },
    [],
  );
  // Page guard
  useEffect(() => {
    if (pagination?.page > totalPages && totalPages > 0) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [totalPages, pagination?.page]);

  const allowedFields = EAllowedFieldsForExport[EExportPage.MMS_SALES] || [];
  const isDatesSelected = !!(startDate && endDate);
  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.SALES}
          subtitle={STRINGS.SALES_SUB}
          icon={getNavItemIcon(NAV_ITEMS.SALES)}
          count={paginationResponse?.totalItems}
          actionsSlot={
            <GlobalSearch
              className="w-[240px]"
              placeholder={
                MMS_SALES_PAGE_UI_PROPS.SEARCH.SOURCE_ORDER_ID_PLACEHOLDER
              }
              globalSearch={sourceOrderId}
              setGlobalSearch={onHandleSourceOrderIdSearch}
              isDataFound={!isEmpty(data)}
              dataTestId={tid(TID.SEARCH_INPUT)}
            />
          }
          actionButtons={
            <DownloadReport
              testId={tid(TID.EXPORT_BTN)}
              label={EReportLabel.EXPORT_DATA}
              type={EReportFormat.CSV}
              url={API_URL_CONFIG?.MMS_SALES?.GET_DOWNLOAD_PDF_CSV}
              orderBy={sorting?.orderBy}
              order={sorting?.order}
              operatorId={selection?.operator?.id}
              deviceId={selection?.device?.id}
              marketId={selection?.market?.id}
              dateFilterBy={dateFilterBy?.queryParam}
              sourceOrderId={sourceOrderId}
              startDate={startDate as string}
              endDate={endDate as string}
              timeZone={selectedTimezone}
              limit={paginationResponse?.totalItems}
              fileName={REPORT_FILE_NAMES.MMS_SALES_REPORT}
              requestMethod={ERequestMethods.POST}
              modalTitle={MODAL_TITLES.MMS_SALES_PAGE}
              supportingReportFormats={exportFormats}
              selectiveFieldExport
              allowedFieldsForExport={allowedFields}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleSelectionChange}
              showProvider={false}
              showOperator
              showLocation={false}
              showMarket
              showDevice
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={(
        <Tooltip placement='left' content={!isDatesSelected ? 'Select date range to apply the filter' : undefined}>
          <div>
            <SegmentedControl
              testId={tid(TID.DATE_FILTER)}
              options={DATE_FILTER_OPTIONS}
              value={dateFilterBy.queryParam}
              onChange={(v) => {
                const found = MMS_SALES_EFILTER.find((f) => f.queryParam === v);
                if (found) setDateFilterBy(found);
              }}
              size="lg"
              shape="square"
              variant="gradient"
              disabled={!isDatesSelected}
            />
          </div>
        </Tooltip>
          )}
        />
      }
    >
      <DataTable<IMmsSalesProps>
        data-testid={tid(TID.TABLE)}
        data={data as unknown as IMmsSalesProps[]}
        loading={loading}
        skeletonRows={15}
        columns={buildColumns(isCopy, onCopyClick)}
        onSort={(orderBy: any, order: any) => {
          setIsMsgOpen({});
          setSorting({ orderBy, order });
        }}
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalItems,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        sortState={sorting as any}
        renderExpandedRow={(item) => (
          <SalesExpandedPanel
            record={saleRecordData as any}
            loading={isExpandLoading}
            headerFields={{
              orderId: (item as IMmsSalesProps)?.orderId,
              marketId: (item as IMmsSalesProps)?.marketId,
            }}
          />
        )}
        expandedRowIndex={expandedRowIndex}
        onExpandChange={(_idx, item) => {
          if (item) {
            onTableRowClick((item as IMmsSalesProps).orderId);
          } else {
            setIsMsgOpen({});
          }
        }}
        rowKey={(item, idx) => (item as any)?.orderId ?? String(idx)}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
      />
    </FullPageCard>
  );
};

export default MmsSales;
